import type { StateCreatorEnhance } from '@halo/zustand-enhance';
import { createEnhance } from '@halo/zustand-enhance';
import type { WalletStoreInitOptions } from '@halo/wallet-app';
import { SessionsStatus, useAppStore, WsStatus } from '@/store/client';
import type { LoginParams } from '@/configs/apis/user';
import { userApis } from '@/configs/apis/user';
import type { User } from '@/configs/apis/user/types';
import Paths from '@/configs/router/paths';
import { checkUrlFromAccountCenter, getDeviceId } from '@/pages/login/helpers';
import { useWalletStore } from '@/wallet';
import { WalletCloud } from '@/scripts/walletCloud';
import { deleteFCMToken } from '@/configs/firebase/config';
import { clearAll } from '@/scripts/debug';
import { DeleteStepsEnum, statusEnum } from '@/pages/main/settings/security/DeleteSteps';
import { type UploadArgsDataKeys } from '@/scripts/cloud/types';
import { OAuth } from '@/pages/login/OAuth';
import { debugInitPromise } from '@/scripts/debug/init';
import { useTransferStore } from '../transfer';
import { useSettingsStore } from '../settings';

export interface SessionContextState {
  user: User | null;
}

export function getInitState(defaultState?: Partial<SessionContextState>): SessionContextState {
  const state: SessionContextState = {
    user: null,
  };

  if (defaultState) {
    for (const k in defaultState) {
      const v = defaultState[k as keyof SessionContextState];
      if (v !== undefined) {
        (state as any)[k] = v;
      }
    }
  }

  return state;
}

export interface SessionStore {
  walletCloud: WalletCloud | null;
  loginType: 'QRCode' | 'Google' | 'Apple' | null;
  session: SessionContextState;
  loading: boolean;
  networkConnecting: boolean;
  retryConnecting: boolean;
  connecting: boolean;
  sessionsLoading: boolean;
  tempAuthInfo: { access_token: string; ckWebAuthToken?: string } | null;
  setRetryConnecting: (retryConnecting: boolean) => void;
  initSession: (params?: { deviceId?: string }) => Promise<void>;
  setSession: (session: Partial<SessionContextState>) => void;
  setLoading: (loading: boolean) => void;
  login: (
    data: LoginParams & {
      clientUid: string;
      device_id?: string;
      isSessionStorage?: boolean;
    }
  ) => Promise<void>;
  logout: () => Promise<void>;
  logOff: (
    deleteValues: DeleteStepsEnum[],
    callback?: (value: { value: DeleteStepsEnum; status?: statusEnum }) => Promise<void>
  ) => Promise<void>;
  setTempAuthInfo: (tempAuthInfo?: { access_token: string }) => void;
}

const setSession: SessionStore['setSession'] = (session) => {
  useSessionStore.setState((store) => {
    Object.assign(store.session, session);
  });
};

const setLoading: SessionStore['setLoading'] = (loading) => {
  useSessionStore.setState({ loading });
};

const initSession: SessionStore['initSession'] = async ({ deviceId } = {}) => {
  setLoading(true);
  try {
    if (userApis.isLogin()) {
      const { initApp } = useAppStore.getState();
      await debugInitPromise;
      await initApp({
        userId: userApis.getUserId(),
        orgId: 'default',
        seed: userApis.getClientUid(),
        token: userApis.getToken(),
        deviceId: getDeviceId(deviceId),
      });

      const { authInfo, cloudInfo, type } = userApis?.getAuthParams() ?? {};
      const initInfo: WalletStoreInitOptions = {
        userId: userApis.getUserId(),
        token: userApis.getToken(),
      };

      if (authInfo && cloudInfo && type) {
        const { ckWebAuthToken } = authInfo;
        const { platform, cid } = cloudInfo;
        const walletCloud = new WalletCloud({
          type: platform,
          key: import.meta.env.VITE_APP_FILE_NAME,
          clientParams:
            platform === type.toLowerCase() ? OAuth.getGoogleConfig() : OAuth.getAppleConfig(),
        });

        await walletCloud.init({
          cid,
          authInfo,
          clientParams: {
            ckWebAuthToken,
          } as any,
        });

        useSessionStore.setState({ walletCloud, loginType: type });
      }

      await useWalletStore.init(initInfo).catch(() => null);
      useSettingsStore.getState().syncBackupCloudData();

      const { accounts } = useWalletStore.getState();
      const { client } = useAppStore.getState();
      if (client?.Api && accounts?.length) {
        client?.Api.common.getUserAddress({ user_id: initInfo.userId! }).then((res) => {
          if (Object.keys(res.data.wallet_address).length === 1) {
            const account = accounts?.find((account) => account.isDefault && account?.uid);
            const derivedAccount = account?.derivedWallets?.[0];
            const walletAddresses = Object.values(derivedAccount?.seriesWallets || {})?.reduce(
              (addresses, wallet) => {
                return {
                  ...addresses,
                  [wallet.chainType]: (wallet as any).walletAddress,
                };
              },
              {}
            );
            client?.Api.common
              .updateUserAddress({ wallet_addresses: walletAddresses })
              .catch((_) => '');
          }
        });
      }
      useTransferStore.getState().initWalletTransfersSend();

      setSession({
        user: {
          ...useAppStore.getState().user,
          user_id: userApis.getUserId(),
        },
      });
    }
  } finally {
    setLoading(false);
  }
};

const login: SessionStore['login'] = async (params) => {
  setLoading(true);
  try {
    const data = await userApis.login(params);
    userApis.setTokens(data, !!params?.isSessionStorage);
    userApis.setUserId(data.user_id);
    userApis.setClientUid(params.clientUid!);
    userApis.setIsNewUser(data?.is_new_user);
    await initSession({ deviceId: params.device_id });
  } finally {
    setLoading(false);
  }
};

const logout: SessionStore['logout'] = async () => {
  setLoading(true);
  try {
    const { client } = useAppStore.getState();
    await client?.Api.common.logout({ device_id: getDeviceId() }).catch(() => null);
    await userApis.removeUserStorage().catch(() => null);
    await deleteFCMToken().catch(() => null);
  } finally {
    location.href = Paths.LOGIN;
    setLoading(false);
  }
};
const logOff: SessionStore['logOff'] = async (deleteValues, callback) => {
  setLoading(true);
  try {
    await callback?.({ value: DeleteStepsEnum.ONE });
    const { walletCloud } = useSessionStore.getState();
    const { client } = useAppStore.getState();
    const logOffRes: any = await client?.Api.common.logOff();

    if (logOffRes?.code !== 1) {
      await callback?.({ value: DeleteStepsEnum.ONE, status: statusEnum.error });
    }

    if (deleteValues.length) {
      const keys: UploadArgsDataKeys[] = [];
      if (
        deleteValues.includes(DeleteStepsEnum.TWO) &&
        deleteValues.includes(DeleteStepsEnum.THREE)
      ) {
        await callback?.({ value: DeleteStepsEnum.THREE });
        keys.push('wallet', 'contacts');
      } else if (deleteValues.includes(DeleteStepsEnum.TWO)) {
        await callback?.({ value: DeleteStepsEnum.TWO });
        keys.push('wallet');
      } else if (deleteValues.includes(DeleteStepsEnum.THREE)) {
        await callback?.({ value: DeleteStepsEnum.THREE });
        keys.push('contacts');
      }
      await walletCloud?.deleteCloudData({ keys }).catch(async () => {
        for (const key of deleteValues) {
          await callback?.({
            value: key,
            status: statusEnum.error,
          });
        }
      });
    }

    await callback?.({ value: DeleteStepsEnum.FOUR });
    await deleteFCMToken().catch(console.error);

    await callback?.({ value: DeleteStepsEnum.FIVE });
    await clearAll().catch(console.error);

    await callback?.({ value: DeleteStepsEnum.SIX });
    location.href = Paths.LOGIN;
  } finally {
    setLoading(false);
  }
};

const setRetryConnecting: SessionStore['setRetryConnecting'] = (retryConnecting) => {
  useSessionStore.setState({ retryConnecting });
};

const setTempAuthInfo: SessionStore['setTempAuthInfo'] = (tempAuthInfo) => {
  useSessionStore.setState({ tempAuthInfo });
};
const createSessionStore: StateCreatorEnhance<SessionStore> = () => ({
  walletCloud: null,
  loginType: null,
  session: getInitState(),
  loading: false,
  networkConnecting: false,
  retryConnecting: false,
  connecting: false,
  sessionsLoading: false,
  tempAuthInfo: null,
  // Actions
  setSession,
  setLoading,
  initSession,
  login,
  logout,
  logOff,
  setRetryConnecting,
  setTempAuthInfo,
});

export const useSessionStore = createEnhance(createSessionStore);

const isFromAccountCenter = checkUrlFromAccountCenter();
if (!isFromAccountCenter) {
  useSessionStore.getState().initSession();
}

useAppStore.subscribe(async (store) => {
  if (store.networkErrorCode === 401) {
    await logout();
  }

  const networkConnecting =
    store.wsStatus === WsStatus.Connecting || store.wsStatus === WsStatus.Closed;
  const sessionsLoading = store.sessionsStatus !== SessionsStatus.READY;

  useSessionStore.setState({
    sessionsLoading,
    networkConnecting,
  });
});

useSessionStore.subscribe((store) => {
  const connecting = store.networkConnecting || store.networkConnecting;
  if (connecting !== store.connecting) {
    useSessionStore.setState({
      connecting,
    });
  }
});
